import React, { useMemo } from 'react';

import { Box, ClassNameMap, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowDownwardOutlined } from '@mui/icons-material';
import { ProviderMedicalRole } from '@uplift/types';
import { L10nHTML } from '@uplift/hooks';
import { useEthnicitiesQuery, useFaithsQuery, useGendersQuery } from '@uplift/queries';

const EATING_DISORDERS = '26e1641d-9334-4e3a-b578-b4bead3891b7';
const SUBSTANCE_ABUSE = '15ade19a-e1a6-4fa5-94a1-24fe5a5b2f73';

export type NoProvidersProps = {
  noInsuranceProviders?: boolean;
  medicalRole?: ProviderMedicalRole;
  numProviders?: number;
  classes?: Partial<ClassNameMap<'container'>>;
  specialtyMatches?: string[];
  missingExperienceFilters?: string[];
};

export const NoProviders: React.VFC<NoProvidersProps> = props => {
  const { classes } = useNoProvidersStyles(props, { props });
  const { data: genders = [] } = useGendersQuery();
  const { data: faiths = [] } = useFaithsQuery();
  const { data: ethnicities = [] } = useEthnicitiesQuery();

  const providerType = props.medicalRole === ProviderMedicalRole.therapist ? 'therapist' : 'provider';
  const typePlural = `${providerType}${props.numProviders === 1 ? '' : 's'}`;

  const { line1, line2 } = useMemo(() => {
    // specialty matches
    if (props.specialtyMatches?.length) {
      const needs = [];

      if (props.specialtyMatches?.includes(EATING_DISORDERS)) {
        needs.push('<b>Eating Disorders</b>');
      }

      if (props.specialtyMatches?.includes(SUBSTANCE_ABUSE)) {
        needs.push('<b>Addiction</b>');
      }

      // specialty matches selected, cash pay only
      if (props.noInsuranceProviders && !props.numProviders) {

        return {
          line1: <Typography variant="h3">No matches who take your insurance—but you still have options</Typography>,
          line2: (
            <Typography>
              We found <b>{props.numProviders}</b> {typePlural} who
              specialize{props.numProviders === 1 ? 's' : ''} in <L10nHTML>{needs.join(' and ')}</L10nHTML> but don&apos;t
              take your insurance. You can still book sessions with them at our <b>reduced self-pay rate</b>.
            </Typography>
          )
        };
      }

      // specialty matches selected, no matches found
      return {
        line1: <Typography variant="h3">No exact matches</Typography>,
        line2: (
          <Typography>
            No {providerType}s who specialize in <L10nHTML>{needs.join(' and ')}</L10nHTML> are available in the next 2 weeks.
          </Typography>
        )
      };
    }

    if (props.missingExperienceFilters?.length) {
      const experiences = genders.concat(ethnicities).concat(faiths);
      const missing = experiences.reduce((acc, cur) => {
        if (props.missingExperienceFilters.includes(cur.id)) {
          acc.push(`<b>${cur.name}</b>`);
        }

        return acc;
      }, []);

      if (!props.numProviders) {
        return {
          line1: <Typography variant="h3">No exact matches</Typography>,
          line2: (
            <Typography>
              Try changing your search filters to find therapists with availability in the next 2 weeks.
            </Typography>
          )
        };
      }

      return {
        line1: <Typography variant="h3">No exact matches&mdash;but you still have options</Typography>,
        line2: (
          <Typography>
            We found {props.numProviders} {typePlural} who match most of your needs
            but {props.numProviders === 1 ? "doesn't" : "don't"} identify as <L10nHTML>{missing.join(' or ')}</L10nHTML>
          </Typography>
        )
      };
    }

    if (props.noInsuranceProviders) {
      return {
        line1: <Typography variant="h3">No matches who take your insurance—but you still have options</Typography>,
        line2: (
          <Typography>
            We found <b>{props.numProviders}</b> {typePlural} who match your
            needs but don&apos;t take your insurance. You can still book sessions with them at our <b>reduced self-pay rate</b>.
          </Typography>
        )
      };
    }

    return {
      line1: <Typography variant="h3">No exact matches&mdash;try a similar search</Typography>,
      line2: (
        <Typography>
          No therapists who exactly fit your criteria are available in the next 2 weeks. We&apos;re adding more therapists
          on UpLift, so you can check again later or try changing your search filters to find someone sooner who matches
          most of your needs.
        </Typography>
      )
    };
  }, [ props.noInsuranceProviders, props.specialtyMatches, props.numProviders, props.missingExperienceFilters, typePlural, providerType, genders, ethnicities, faiths ]);

  return (
    <Box mt={props.noInsuranceProviders ? 0 : 5}>
      <Box className={classes.textContainer}>
        {line1}

        <Box mt={1}>
          {line2}
        </Box>
      </Box>
      {!!props.noInsuranceProviders && (
        <Box className={classes.arrowDown}>
          <ArrowDownwardOutlined fontSize="large" htmlColor="#757575"/>
        </Box>
      )}
    </Box>
  );
};

const useNoProvidersStyles = makeStyles<{ classes?: Partial<ClassNameMap<'container'>> }>()(theme => ({
  tip: {
    background: theme.palette.secondary.background,
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
  },
  helpLink: {
    fontWeight: 600,
    textDecorationThickness: '1px',
    textUnderlineOffset: '1px',
    color: '#B62289',
  },
  textContainer: {
    position: 'relative',

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing( 0, 2, 2, 2)
    }
  },
  arrow: {
    position: 'absolute',
    right: 'calc(100% - 25px)',
    top: -56,
    transformOrigin: 'bottom right',
    transform: 'rotateZ(13deg)',

    [theme.breakpoints.up('md')]: {
      transform: 'rotateZ(0deg)',
    },
  },
  arrowDown: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(0),

    }
  },
  needHelp: {
    marginTop: theme.spacing(4),
  }
}));
